import type {ProjectScopedScope, Scope} from "./scopes";

/**
 * Which credential may call an action.
 *
 * - a non-empty tuple of scopes: the set this action may require. Every entry is required unless
 *   `getScopes` narrows it per call. Usually one, naming what the action writes (`["deck:write"]`),
 *   and always the most specific scope that fits: `accountRole:write` over `account:write`,
 *   `projectTag:write` over `project:write`.
 * - `"session"`: the credential's own person is the subject and there is no operation to name —
 *   logging out, changing a password, leaving the org, one's own hints, emails and tokens. Sessions
 *   and legacy tokens, never an api token. An operation a token must not perform is *not* this:
 *   that is a scope in `HUMAN_ONLY_SCOPES`, so the declaration stays the only wall.
 * - `"public"`: no authentication at all.
 */
export type ActionScopes = "public" | "session" | readonly [Scope, ...Scope[]];

/**
 * Whether a tuple names a scope that is answered per project, which decides both what the server's
 * `resolve` has to return and whether the app has to name a project when it asks.
 */
export type IsProjectScoped<Sc extends ActionScopes> = Sc extends readonly Scope[]
  ? Extract<Sc[number], ProjectScopedScope> extends never
    ? false
    : true
  : false;

/**
 * What every dispatch action declares, so the app can ask the question the server will answer
 * without a request: `hasScopeForMutation(root, "decks.update", {project})` reads the row below and
 * holds every scope in it against the caller's own set.
 *
 * Hand-maintained, and kept honest by `action-scopes-test.ts` in node-api, which compares every row
 * to what `actionCollections` declares and fails on a row that is missing, extra or stale.
 *
 * `narrows: true` marks an action with a `getScopes`: the tuple is then what the action *may*
 * require, not what it does, so a caller passes the subset it means — `preferredOrder` on
 * `decks.update` needs `project:write`, everything else on that form does not.
 */
export const ACTION_SCOPES = {
  "accountApiTokens.create": {scopes: ["accountApiToken:write"]},
  "accountApiTokens.revoke": {scopes: ["accountApiToken:write"]},
  "accountApiTokens.update": {scopes: ["accountApiToken:write"]},
  "accounts.disable": {scopes: "session"},
  "accounts.update": {scopes: ["account:write", "billing:write"], narrows: true},
  "accounts.updateSettings": {
    scopes: ["accountUserSetting:writeOwn", "accountUserSetting:writeAny"],
    narrows: true,
  },
  "affiliateCodes.create": {scopes: ["affiliateCode:write"]},
  "affiliateCodes.delete": {scopes: ["affiliateCode:write"]},
  "affiliateCodes.update": {scopes: ["affiliateCode:write"]},
  "attachments.batchDeleteByFileId": {scopes: ["file:delete"]},
  "attachments.batchDeleteFile": {scopes: ["file:delete"]},
  "attachments.delete": {scopes: ["attachment:write"]},
  "attachments.deleteFile": {scopes: ["file:delete"]},
  "attachments.update": {scopes: ["attachment:write"]},
  "billing.addCreditCard": {scopes: ["billing:write"]},
  "billing.cancelSubscription": {scopes: ["billing:write"]},
  "billing.changeBillingInterval": {scopes: ["billing:write"]},
  "billing.createSubscription": {scopes: ["billing:write"]},
  "billing.downgradeSubscription": {scopes: ["billing:write"]},
  "billing.revokePendingIntervalChange": {scopes: ["billing:write"]},
  "billing.revokeSubscriptionCancellation": {scopes: ["billing:write"]},
  "billing.revokeSubscriptionDowngrade": {scopes: ["billing:write"]},
  "billing.updatePurchasedSeats": {scopes: ["billing:write"]},
  "billing.upgradeSubscription": {scopes: ["billing:write"]},
  "bookmarks.addCards": {scopes: ["handCard:writeOwn", "handCard:writeAny"], narrows: true},
  "bookmarks.removeCards": {scopes: ["handCard:writeOwn", "handCard:writeAny"], narrows: true},
  "bookmarks.setOrders": {scopes: ["handCard:writeOwn", "handCard:writeAny"], narrows: true},
  "cardOrders.addAfter": {scopes: ["cardOrder:write"]},
  "cardOrders.addBefore": {scopes: ["cardOrder:write"]},
  "cardOrders.updateLabel": {scopes: ["cardOrder:write"]},
  "cardPresets.create": {scopes: ["cardPreset:write"]},
  "cardPresets.delete": {scopes: ["cardPreset:write"]},
  "cards.addFile": {scopes: ["card:write"]},
  "cards.bulkCreate": {scopes: ["card:write"]},
  "cards.bulkImport": {scopes: ["card:write"]},
  "cards.bulkUpdate": {scopes: ["card:write"]},
  "cards.copy": {scopes: ["card:write"]},
  "cards.create": {scopes: ["card:write"]},
  "cards.createMany": {scopes: ["card:write"]},
  "cards.removeAttachmentFile": {scopes: ["card:write"]},
  "cards.seen": {scopes: ["card:write"]},
  "cards.update": {scopes: ["card:write"]},
  "cards.updateMany": {scopes: ["card:write"]},
  "decks.addToSpaceAfter": {scopes: ["deck:write"]},
  "decks.addToSpaceBefore": {scopes: ["deck:write"]},
  "decks.create": {scopes: ["deck:write"]},
  "decks.delete": {scopes: ["deck:delete"]},
  "decks.renameZone": {scopes: ["deck:write", "cardOrder:write"]},
  "decks.update": {scopes: ["deck:write", "project:write"], narrows: true},
  "decks.updateCover": {scopes: ["deck:write"]},
  "decks.updateGuardians": {scopes: ["deckGuardian:write"]},
  "discordIntegration.createProjectNotification": {scopes: ["integration:write"]},
  "discordIntegration.createSlashCommand": {scopes: ["integration:write"]},
  "discordIntegration.deleteProjectNotification": {scopes: ["integration:write"]},
  "discordIntegration.deleteSlashCommand": {scopes: ["integration:write"]},
  "discordIntegration.recheckChannelProblems": {scopes: ["integration:write"]},
  "discordIntegration.removeGuild": {scopes: ["integration:write"]},
  "discordIntegration.setChannelProblemsDismissed": {scopes: ["integration:write"]},
  "discordIntegration.updateGuild": {scopes: ["integration:write"]},
  "discordIntegration.updateProjectNotification": {scopes: ["integration:write"]},
  "discordIntegration.updateSlashCommand": {scopes: ["integration:write"]},
  "files.create": {scopes: ["file:write"]},
  "forecastOverwrites.create": {scopes: ["forecastOverwrite:write"]},
  "forecastOverwrites.remove": {scopes: ["forecastOverwrite:write"]},
  "forecastOverwrites.update": {scopes: ["forecastOverwrite:write"]},
  "githubIntegration.addHook": {scopes: ["integration:write"]},
  "githubIntegration.addHookViaRepoId": {scopes: ["integration:write"]},
  "githubIntegration.removeHook": {scopes: ["integration:write"]},
  "githubIntegration.removeIntegration": {scopes: ["integration:write"]},
  "handQueue.addCardsToHand": {scopes: ["queueEntry:write"]},
  "handQueue.addCardsToOwner": {scopes: ["queueEntry:write"]},
  "handQueue.removeCards": {scopes: ["queueEntry:write"]},
  "handQueue.setCardOrders": {scopes: ["queueEntry:write"]},
  "handQueue.setQueueSelection": {
    scopes: ["queueSelection:writeOwn", "queueSelection:writeAny"],
    narrows: true,
  },
  "hints.dismiss": {scopes: "session"},
  "hints.undismiss": {scopes: "session"},
  "inviteCodes.apply": {scopes: "public"},
  "inviteCodes.applyExisting": {scopes: "session"},
  "inviteCodes.create": {scopes: ["accountRole:write"]},
  "inviteCodes.disable": {scopes: ["accountRole:write"]},
  "jiraIntegration.createProjectSync": {scopes: ["integration:write"]},
  "jiraIntegration.deleteProjectSync": {scopes: ["integration:write"]},
  "jiraIntegration.updateProjectSync": {scopes: ["integration:write"]},
  "lastSeenUpvotes.justSeen": {scopes: ["lastSeenCardUpvote:write"]},
  "milestones.create": {scopes: ["milestone:write"]},
  "milestones.delete": {scopes: ["milestone:write"]},
  "milestones.pin": {
    scopes: ["pinnedMilestone:writeOwn", "pinnedMilestone:writeAny"],
    narrows: true,
  },
  "milestones.renameZone": {scopes: ["milestone:write", "cardOrder:write"]},
  "milestones.update": {scopes: ["milestone:write"]},
  "notifications.addBrowserSubscription": {scopes: "session"},
  "notifications.dismissAutoFinishedTimeTrackingSegment": {scopes: "session"},
  "notifications.dismissCardDiffs": {scopes: ["cardDiffNotification:writeOwn"]},
  "notifications.dismissResolvables": {scopes: ["resolvableNotification:writeOwn"]},
  "notifications.removeBrowserSubscription": {scopes: "session"},
  "notifications.seenResolvableEntries": {scopes: ["resolvableNotification:writeOwn"]},
  "notifications.setChannels": {scopes: ["userNotificationChannel:writeOwn"]},
  "notifications.setRemindMeOn": {scopes: ["resolvableNotification:writeOwn"]},
  "onboarding.progress": {scopes: "session"},
  "passkeys.beginRegistration": {scopes: "session"},
  "passkeys.delete": {scopes: "session"},
  "passkeys.finishRegistration": {scopes: "session"},
  "passkeys.rename": {scopes: "session"},
  "projectSelections.set": {
    scopes: ["projectSelection:writeOwn", "projectSelection:writeAny"],
    narrows: true,
  },
  "projects.addTag": {scopes: ["projectTag:write"]},
  "projects.copyTags": {scopes: ["projectTag:write"]},
  "projects.create": {scopes: ["project:create"]},
  "projects.createWithDecks": {scopes: ["project:create"]},
  "projects.deleteTags": {scopes: ["projectTag:write"]},
  "projects.setVisibility": {scopes: ["project:write", "project:delete"], narrows: true},
  "projects.update": {
    scopes: ["project:write", "project:writeAccess", "projectUser:write"],
    narrows: true,
  },
  "projects.updateCover": {scopes: ["project:write"]},
  "projects.updateOrder": {
    scopes: ["projectOrder:writeOwn", "projectOrder:writeAny"],
    narrows: true,
  },
  "projects.updateTag": {scopes: ["projectTag:write"]},
  "publicProjectMembership.create": {scopes: "session"},
  "publicProjectMembership.delete": {scopes: "session"},
  "publicProjectMembership.update": {scopes: "session"},
  "recoveryCodes.regenerate": {scopes: "session"},
  "resolvables.addReaction": {scopes: ["resolvableEntryReaction:writeOwn"]},
  "resolvables.close": {scopes: ["resolvable:write", "card:write"], narrows: true},
  "resolvables.comment": {scopes: ["resolvableEntry:writeOwn"]},
  "resolvables.create": {scopes: ["resolvable:write", "card:write"], narrows: true},
  "resolvables.removeReaction": {
    scopes: ["resolvableEntryReaction:writeOwn", "resolvableEntryReaction:writeAny"],
    narrows: true,
  },
  "resolvables.reopen": {scopes: ["resolvable:write", "card:write"], narrows: true},
  "resolvables.updateComment": {
    scopes: ["resolvableEntry:writeOwn", "resolvableEntry:writeAny"],
    narrows: true,
  },
  "resolvables.updateParticipantDone": {
    scopes: ["resolvableParticipant:writeOwn", "resolvableParticipant:writeAny"],
    narrows: true,
  },
  "roles.addProjectAccess": {scopes: ["projectUser:write"]},
  "roles.delete": {scopes: ["accountRole:write"]},
  "roles.disable": {scopes: ["accountRole:write"]},
  "roles.leave": {scopes: "session"},
  "roles.reEnable": {scopes: ["accountRole:write"]},
  "roles.revokeProjectAccess": {scopes: ["projectUser:write"]},
  "roles.setAddonRoles": {scopes: ["accountRole:write"]},
  "roles.transferOwnership": {scopes: "session"},
  "roles.update": {scopes: ["accountRole:write"]},
  "scmWebhooks.create": {scopes: ["integration:write"]},
  "scmWebhooks.remove": {scopes: ["integration:write"]},
  "search.delete": {scopes: ["savedSearch:writeOwn"]},
  "search.save": {scopes: ["savedSearch:writeOwn"]},
  "secondFactor.beginPasskey": {scopes: "public"},
  "secondFactor.cancel": {scopes: "public"},
  "secondFactor.getChallenge": {scopes: "public"},
  "secondFactor.verifyPasskey": {scopes: "public"},
  "secondFactor.verifyRecoveryCode": {scopes: "public"},
  "secondFactor.verifyTotp": {scopes: "public"},
  "slackIntegration.remove": {scopes: ["integration:write"]},
  "slackIntegration.update": {scopes: ["integration:write"]},
  "sprints.createConfig": {scopes: ["sprintConfig:write"]},
  "sprints.renameZone": {scopes: ["sprint:write", "cardOrder:write"]},
  "sprints.updateConfig": {scopes: ["sprintConfig:write"]},
  "sprints.updateSprint": {scopes: ["sprint:write"]},
  "timeTracking.close": {scopes: ["timeTrackingSegment:writeOwn"]},
  "timeTracking.create": {
    scopes: ["timeTrackingSegment:writeOwn", "timeTrackingSegment:writeAny"],
    narrows: true,
  },
  "timeTracking.delete": {
    scopes: ["timeTrackingSegment:writeOwn", "timeTrackingSegment:writeAny"],
    narrows: true,
  },
  "timeTracking.setModifyDuration": {
    scopes: ["timeTrackingSegment:writeOwn", "timeTrackingSegment:writeAny"],
    narrows: true,
  },
  "timeTracking.start": {
    scopes: ["timeTrackingSegment:writeOwn", "timeTrackingSegment:writeAny", "card:write"],
    narrows: true,
  },
  "timeTracking.stop": {
    scopes: ["timeTrackingSegment:writeOwn", "timeTrackingSegment:writeAny"],
    narrows: true,
  },
  "totp.beginSetup": {scopes: "session"},
  "totp.confirmSetup": {scopes: "session"},
  "totp.delete": {scopes: "session"},
  "userApiTokens.create": {scopes: "session"},
  "userApiTokens.revoke": {scopes: "session"},
  "userApiTokens.revokeAsAdmin": {scopes: ["accountApiToken:write"]},
  "userApiTokens.setEnabledForAccount": {scopes: ["accountApiToken:write"]},
  "userApiTokens.update": {scopes: "session"},
  "userEmails.add": {scopes: "session"},
  "userEmails.delete": {scopes: "session"},
  "userEmails.resendVerification": {scopes: "session"},
  "userEmails.setPrimary": {scopes: "session"},
  "userEmails.verify": {scopes: "public"},
  "userReport.createSettings": {scopes: ["integration:write"]},
  "userReport.deleteSettings": {scopes: ["integration:write"]},
  "userReport.regenerateSettingsToken": {scopes: ["integration:write"]},
  "userReport.updateSettings": {scopes: ["integration:write"]},
  "userReport.updateToken": {scopes: ["integration:write"]},
  "userTags.add": {scopes: ["userTag:writeOwn"]},
  "userTags.delete": {scopes: ["userTag:writeOwn"]},
  "userTags.update": {scopes: ["userTag:writeOwn"]},
  "users.beginResetLogin": {scopes: "public"},
  "users.beginReverifyPasskey": {scopes: "session"},
  "users.changePassword": {scopes: "session"},
  "users.finishReverifyPasskey": {scopes: "session"},
  "users.getReverifyMethods": {scopes: "session"},
  "users.joinAccount": {scopes: "public"},
  "users.login": {scopes: "public"},
  "users.logout": {scopes: "session"},
  "users.requestPasswordReset": {scopes: "public"},
  "users.resendInvitation": {scopes: ["accountRole:write"]},
  "users.resetPassword": {scopes: "public"},
  "users.reverify": {scopes: "session"},
  "users.reverifyTotp": {scopes: "session"},
  "users.revokeInvitation": {scopes: ["accountRole:write"]},
  "users.sendInvitation": {scopes: ["accountRole:write"]},
  "users.update": {scopes: ["user:writeOwn"]},
  "visionBoard.createBoard": {scopes: ["visionBoard:write"]},
  "visionBoard.createQuery": {scopes: ["visionBoardQuery:write"]},
  "visionBoard.deleteBoard": {scopes: ["visionBoard:write"]},
  "visionBoard.duplicateQueries": {scopes: ["visionBoardQuery:write"]},
  "visionBoard.markQueryStale": {scopes: ["visionBoardQuery:write"]},
  "visionBoard.reloadQuery": {scopes: "public"},
  "visionBoard.updateBoard": {scopes: ["visionBoard:write"]},
  "votes.unvote": {scopes: ["cardUpvote:writeOwn"]},
  "votes.vote": {scopes: ["cardUpvote:writeOwn"]},
  "watchings.addCard": {scopes: ["cardSubscription:writeOwn"]},
  "watchings.addDeck": {
    scopes: ["deckSubscription:writeOwn", "deckSubscription:writeAny"],
    narrows: true,
  },
  "watchings.removeCard": {scopes: ["cardSubscription:writeOwn"]},
  "watchings.removeDeck": {
    scopes: ["deckSubscription:writeOwn", "deckSubscription:writeAny"],
    narrows: true,
  },
  "wizards.finish": {scopes: ["wizard:write"]},
  "wizards.progress": {scopes: ["wizard:write"]},
  "wizards.skip": {scopes: ["wizard:write"]},
  "workflowTemplates.clone": {scopes: ["deck:write", "workflowItem:write"]},
  "workflowTemplates.create": {scopes: ["workflowTemplate:write"]},
  "workflowTemplates.delete": {scopes: ["workflowTemplate:write"]},
  "workflowTemplates.update": {scopes: ["workflowTemplate:write"]},
  "workflows.addItemsAfter": {scopes: ["workflowItem:write"]},
  "workflows.addItemsBefore": {scopes: ["workflowItem:write"]},
  "workflows.apply": {scopes: ["card:write"]},
  "workflows.bulkUpdateItems": {scopes: ["workflowItem:write"]},
  "workflows.copyWorkflowItems": {scopes: ["workflowItem:write"]},
  "workflows.createDependencyChain": {scopes: ["workflowItem:write"]},
  "workflows.createItem": {scopes: ["workflowItem:write"]},
  "workflows.renameItemZone": {scopes: ["deck:write", "workflowItem:write"]},
  "workflows.updateItem": {scopes: ["workflowItem:write"]},
} as const satisfies Record<string, {scopes: ActionScopes; narrows?: true}>;

export type ActionKey = keyof typeof ACTION_SCOPES;
